// Domain + form types for the Questions tab (sections + questions).

export type ValueType =
  | "text"
  | "number"
  | "date"
  | "checkbox"
  | "email"
  | "phone";

export const VALUE_TYPE_OPTIONS: { value: ValueType; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "checkbox", label: "Checkbox" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
];

export const humanizeValueType = (v: ValueType | string): string =>
  VALUE_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? String(v);

// number "Decimal Places": wire value (int) -> label.
export const STEP_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "1" },
  { value: 1, label: "0.1" },
  { value: 2, label: "0.01" },
];

// date min/max preset dropdowns (wire value -> label), distinct per side.
export const DATE_MIN_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "None" },
  { value: "0.days", label: "Today" },
  { value: "1.month", label: "In a month" },
  { value: "1.quarter", label: "In a quarter" },
  { value: "6.months", label: "In six months" },
  { value: "1.year", label: "In a year" },
  { value: "2.years", label: "Two years" },
];

export const DATE_MAX_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "None" },
  { value: "Date.today", label: "Today" },
  { value: "1.month.from_now", label: "In a month" },
  { value: "1.quarter.from_now", label: "In a quarter" },
  { value: "6.months.from_now", label: "In six months" },
  { value: "1.year.from_now", label: "In a year" },
  { value: "2.years.from_now", label: "Two years" },
];

// Which value-types collect min/max, step, and select-options.
export const hasMinMax = (v: ValueType): boolean =>
  v === "text" || v === "number" || v === "date";
export const hasStep = (v: ValueType): boolean => v === "number";
export const hasSelectOptions = (v: ValueType): boolean => v !== "date";

export interface SelectOption {
  key: string;
  value: string;
  default: boolean;
}

export interface QuestionSettings {
  min: string;
  max: string;
  step: number | "";
}

// List view types.
export interface Section {
  id: string;
  name: string;
  expression: string;
  introduction: string;
  position: number;
}

export interface Question {
  id: string;
  question: string;
  name: string;
  slug: string;
  valueType: ValueType;
  required: boolean;
  settings: QuestionSettings;
  selectOptions: SelectOption[];
  sectionId: string;
  position: number;
}

// Form value types.
export interface SectionFormValues {
  name: string;
  expression: string;
  introduction: string;
}

export const emptySectionForm: SectionFormValues = {
  name: "",
  expression: "",
  introduction: "",
};

export interface QuestionFormValues {
  question: string;
  name: string;
  valueType: ValueType;
  required: boolean;
  settings: QuestionSettings;
  selectOptions: SelectOption[];
}

export const emptyQuestionForm: QuestionFormValues = {
  question: "",
  name: "",
  valueType: "text",
  required: true,
  settings: { min: "", max: "", step: "" },
  selectOptions: [],
};
